import React from "react";
import { useIntl } from "react-intl";
import { PeekBox, PeekTypography, useTheme } from "@piiqtechnologies/ui-components";
import { WHISKER_CHART_CLOSE_THRESHOLD, WHISKER_CHART_LOG_FLOOR } from "./constants";
import messages from "./messages.i18n";
function logPosition(value, min, max) {
    const safeVal = Math.max(value, WHISKER_CHART_LOG_FLOOR);
    const safeMin = Math.max(min, WHISKER_CHART_LOG_FLOOR);
    const safeMax = Math.max(max, WHISKER_CHART_LOG_FLOOR);
    const logMin = Math.log10(safeMin);
    const logMax = Math.log10(safeMax);
    if (logMax === logMin)
        return 50;
    return Math.min(100, Math.max(0, ((Math.log10(safeVal) - logMin) / (logMax - logMin)) * 100));
}
const Connector = ({ side }) => (React.createElement(PeekBox, { sx: {
        width: 16,
        height: 2,
        bgcolor: "grey.400",
        position: "relative",
        "&::after": {
            content: '""',
            position: "absolute",
            top: "50%",
            [side === "left" ? "left" : "right"]: 0,
            transform: "translateY(-50%)",
            width: 2,
            height: 16,
            bgcolor: "grey.400",
        },
    } }));
const MinMaxLabel = ({ value, caption }) => (React.createElement(PeekBox, { sx: { textAlign: "center", minWidth: 50 } },
    React.createElement(PeekTypography, { variant: "body1", sx: { fontWeight: "fontWeightMedium", color: "grey.400", lineHeight: 1.4 } }, value),
    React.createElement(PeekTypography, { variant: "caption", sx: { color: "grey.400", fontSize: 12 } }, caption)));
export const WhiskerRangeChart = ({ distribution, yourValue, yourRank }) => {
    const intl = useIntl();
    const theme = useTheme();
    const isEmpty = !distribution;
    const minLabel = intl.formatMessage(messages.minLabel);
    const maxLabel = intl.formatMessage(messages.maxLabel);
    if (isEmpty) {
        return (React.createElement(PeekBox, { sx: { position: "relative", py: 2.5, px: 2 } },
            React.createElement(PeekBox, { sx: { display: "flex", alignItems: "center" } },
                React.createElement(MinMaxLabel, { value: "-", caption: minLabel }),
                React.createElement(Connector, { side: "left" }),
                React.createElement(PeekBox, { sx: { position: "relative", flexGrow: 1 } },
                    React.createElement(PeekBox, { sx: { position: "relative", height: 35, borderRadius: 2, overflow: "hidden", bgcolor: "grey.100" } },
                        React.createElement(PeekBox, { sx: {
                                position: "absolute",
                                height: "100%",
                                width: 4,
                                bgcolor: "grey.400",
                                top: 0,
                                left: "50%",
                                transform: "translateX(-50%)",
                                zIndex: 2,
                            } }))),
                React.createElement(Connector, { side: "right" }),
                React.createElement(MinMaxLabel, { value: "-", caption: maxLabel }))));
    }
    const { min, p25, p50, p75, max } = distribution;
    const q1Pct = logPosition(p25, min, max);
    const medianPct = logPosition(p50, min, max);
    const q3Pct = logPosition(p75, min, max);
    const userPct = logPosition(yourValue, min, max);
    // When within threshold of max, boost marker toward the end of the bar
    const TARGET_MAX_PCT = 100;
    const gapToMax = max - yourValue;
    const adjustedUserPct = gapToMax <= WHISKER_CHART_CLOSE_THRESHOLD
        ? userPct + (TARGET_MAX_PCT - userPct) * (1 - gapToMax / WHISKER_CHART_CLOSE_THRESHOLD)
        : userPct;
    return (React.createElement(PeekBox, { sx: { position: "relative", py: 1, px: 2 } },
        React.createElement(PeekBox, { sx: { display: "flex", alignItems: "center", pt: "44px" } },
            React.createElement(MinMaxLabel, { value: String(Math.round(min)), caption: minLabel }),
            React.createElement(PeekBox, { sx: { position: "relative", flexGrow: 1 } },
                React.createElement(PeekBox, { sx: {
                        position: "absolute",
                        bottom: "100%",
                        mb: 1,
                        left: `${adjustedUserPct}%`,
                        transform: "translateX(-50%)",
                        bgcolor: "white",
                        border: 2,
                        borderColor: "secondary.main",
                        borderRadius: 3,
                        px: 2.5,
                        py: 1,
                        fontWeight: "fontWeightMedium",
                        color: "text.primary",
                        fontSize: 14,
                        whiteSpace: "nowrap",
                        zIndex: 10,
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            bottom: -12,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: 0,
                            height: 0,
                            borderLeft: "12px solid transparent",
                            borderRight: "12px solid transparent",
                            borderTop: `12px solid ${theme.palette.secondary.main}`,
                        },
                    } },
                    intl.formatMessage(messages.youRankLabel, { rank: yourRank }),
                    React.createElement(PeekBox, { sx: {
                            position: "absolute",
                            bottom: -10,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: 0,
                            height: 0,
                            borderLeft: "10px solid transparent",
                            borderRight: "10px solid transparent",
                            borderTop: "10px solid white",
                        } })),
                React.createElement(PeekBox, { sx: {
                        position: "absolute",
                        left: "-1%",
                        width: 0,
                        height: "100%",
                        top: 0,
                        borderLeft: "2px dashed white",
                        zIndex: 5,
                    } }),
                React.createElement(PeekBox, { sx: { position: "relative", height: 35 } },
                    React.createElement(PeekBox, { sx: {
                            position: "absolute",
                            top: "calc(50% - 1px)",
                            left: 0,
                            right: 0,
                            height: 2,
                            bgcolor: theme.palette.grey[300],
                            zIndex: 0,
                        } }),
                    React.createElement(PeekBox, { sx: { position: "absolute", inset: 0, overflow: "hidden", borderRadius: 2 } },
                        React.createElement(PeekBox, { sx: {
                                position: "absolute",
                                height: "100%",
                                top: 0,
                                left: 0,
                                width: `${q1Pct}%`,
                                bgcolor: "transparent",
                                borderRadius: "8px 0 0 8px",
                            } }),
                        React.createElement(PeekBox, { sx: {
                                position: "absolute",
                                height: "100%",
                                top: 0,
                                left: `${q1Pct}%`,
                                width: `${medianPct - q1Pct}%`,
                                bgcolor: theme.palette.teal[100],
                            } }),
                        React.createElement(PeekBox, { sx: {
                                position: "absolute",
                                height: "100%",
                                top: 0,
                                left: `${medianPct}%`,
                                width: `${q3Pct - medianPct}%`,
                                bgcolor: theme.palette.teal[300],
                            } }),
                        React.createElement(PeekBox, { sx: {
                                position: "absolute",
                                height: "100%",
                                top: 0,
                                left: `${q3Pct}%`,
                                width: `${100 - q3Pct}%`,
                                bgcolor: "transparent",
                                borderRadius: "0 8px 8px 0",
                            } }),
                        React.createElement(PeekBox, { sx: {
                                position: "absolute",
                                height: "100%",
                                width: 2,
                                bgcolor: "white",
                                top: 0,
                                left: `${medianPct}%`,
                                zIndex: 2,
                            } })),
                    React.createElement(PeekBox, { sx: {
                            position: "absolute",
                            left: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 2,
                            height: 12,
                            bgcolor: theme.palette.grey[400],
                            zIndex: 3,
                        } }),
                    React.createElement(PeekBox, { sx: {
                            position: "absolute",
                            right: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 2,
                            height: 12,
                            bgcolor: theme.palette.grey[400],
                            zIndex: 3,
                        } }))),
            React.createElement(MinMaxLabel, { value: String(Math.round(max)), caption: maxLabel }))));
};
